import React, { useEffect, useRef, useState } from "react";

const MIN_SCALE = 0.5;
const MAX_SCALE = 6;

/**
 * 将图片缩放比例限制在允许范围内。
 * @param {number} scale 原始缩放比例
 * @returns {number} 限制后的缩放比例
 */
const clampScale = (scale) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/**
 * 渲染支持滚轮缩放、拖动平移和复位的工程图片。
 * @param {{ src?: string, alt: string, label?: string, dark?: boolean }} props 组件属性
 * @returns {React.JSX.Element} 工程图片查看器
 */
const PanZoomAsset = ({ src, alt, label = "IMAGE PREVIEW", dark = false }) => {
  const viewportRef = useRef(null);
  const dragRef = useRef({ active: false, x: 0, y: 0 });
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const [loadError, setLoadError] = useState(false);

  /**
   * 按指定倍数调整当前图片缩放比例。
   * @param {number} factor 缩放倍数
   */
  const zoom = (factor) => {
    setTransform((current) => ({
      ...current,
      scale: clampScale(current.scale * factor),
    }));
  };

  /**
   * 恢复图片的初始缩放和位移。
   */
  const reset = () => {
    setTransform({ scale: 1, x: 0, y: 0 });
  };

  /**
   * 开始记录鼠标或触控拖动位置。
   * @param {React.PointerEvent<HTMLDivElement>} event 指针事件
   */
  const handlePointerDown = (event) => {
    if (event.button !== 0 || event.target.closest("button")) return;
    dragRef.current = { active: true, x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  /**
   * 根据指针移动距离更新图片平移位置。
   * @param {React.PointerEvent<HTMLDivElement>} event 指针事件
   */
  const handlePointerMove = (event) => {
    if (!dragRef.current.active) return;
    const deltaX = event.clientX - dragRef.current.x;
    const deltaY = event.clientY - dragRef.current.y;
    dragRef.current = { active: true, x: event.clientX, y: event.clientY };
    setTransform((current) => ({
      ...current,
      x: current.x + deltaX,
      y: current.y + deltaY,
    }));
  };

  /**
   * 结束当前图片拖动。
   */
  const stopDragging = () => {
    dragRef.current.active = false;
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    /**
     * 使用非被动滚轮监听器实现图片缩放，避免页面同时滚动。
     * @param {WheelEvent} event 滚轮事件
     */
    const handleWheel = (event) => {
      event.preventDefault();
      zoom(event.deltaY < 0 ? 1.1 : 1 / 1.1);
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    setLoadError(false);
    setTransform({ scale: 1, x: 0, y: 0 });
  }, [src]);

  return (
    <div
      ref={viewportRef}
      className={`relative h-[360px] touch-none overflow-hidden ${
        dark ? "bg-[#0d1422]" : "bg-[#fafcff]"
      }`}
      style={{
        backgroundImage: dark
          ? "linear-gradient(#2c354a 1px, transparent 1px), linear-gradient(90deg, #2c354a 1px, transparent 1px)"
          : "linear-gradient(#eaf0f7 1px, transparent 1px), linear-gradient(90deg, #eaf0f7 1px, transparent 1px)",
        backgroundSize: dark ? "54px 54px" : "34px 34px",
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDragging}
      onPointerCancel={stopDragging}
      onLostPointerCapture={stopDragging}
    >
      <div className={`absolute left-4 top-4 z-10 flex items-center gap-2 rounded-md px-3 py-2 text-[9px] font-bold tracking-[0.12em] shadow-sm backdrop-blur ${
        dark ? "bg-[#243045]/95 text-[#c7d5e9]" : "bg-white/90 text-[#7f90a7]"
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-[#54d8a9]" : "bg-[#4a91f6]"}`} />
        {label}
      </div>

      {src && !loadError ? (
        <div
          className="flex h-full w-full items-center justify-center will-change-transform"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
          <img
            src={src}
            alt={alt}
            draggable="false"
            className="max-h-[86%] max-w-[86%] select-none object-contain"
            onError={() => setLoadError(true)}
          />
        </div>
      ) : (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
          {loadError ? "Failed to load image" : "No resource available"}
        </div>
      )}

      {src && !loadError && (
        <div className={`absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-xl p-1.5 shadow-lg ${
          dark ? "bg-[#253149] text-[#dce5f4]" : "bg-white/95 text-[#607592]"
        }`}>
          <button type="button" className={`h-8 w-8 rounded-lg text-lg ${dark ? "hover:bg-[#3d526e]" : "hover:bg-[#eaf1ff] hover:text-[#397eee]"}`} onClick={() => zoom(1 / 1.25)} aria-label="Zoom out">
            −
          </button>
          <span className={`min-w-[48px] text-center text-[11px] font-semibold ${dark ? "text-[#b7c5d9]" : "text-[#76839a]"}`}>
            {Math.round(transform.scale * 100)}%
          </span>
          <button type="button" className={`h-8 w-8 rounded-lg text-lg ${dark ? "hover:bg-[#3d526e]" : "hover:bg-[#eaf1ff] hover:text-[#397eee]"}`} onClick={() => zoom(1.25)} aria-label="Zoom in">
            +
          </button>
          <span className={`mx-1 h-5 w-px ${dark ? "bg-[#485871]" : "bg-[#e5e9f0]"}`} />
          <button type="button" className={`h-8 w-8 rounded-lg text-base ${dark ? "hover:bg-[#3d526e]" : "hover:bg-[#eaf1ff] hover:text-[#397eee]"}`} onClick={reset} aria-label="Reset view" title="Reset view">
            ⌖
          </button>
        </div>
      )}
    </div>
  );
};

export default PanZoomAsset;
